// .scss, not .css: see Fields/fileFiled/uploader.js — a plain .css import is
// never injected in the production bundle.
import './passport.scss'
import geoCountry from '@/common/FF/Fields/geoCountry'

/**
 * Passport field widget: the issuing country picked inside the field (flag + code
 * at its start, like the phone field), and the number checked against that
 * country's format while it is typed.
 *
 * The picker opens a searchable country list (type a name or code; arrows, Enter,
 * Esc). The chosen country is submitted in a hidden `<field>_country` input.
 *
 * The number is kept without spaces and in capitals, the form the server checks
 * and stores. A wrong format:
 *  - sets a custom validity error on the input, which is what submit gates read
 *    (ExoForm's, and a site's own such as mistergold's checkout);
 *  - turns the field red with a message under it, once the visitor has left the
 *    field (never mid-typing), then follows every change.
 * After every check a `change` event is fired on the input: a site gate that
 * listens there ran BEFORE this widget's own handler (it was attached first, at
 * page load), so it would otherwise act on the previous value's validity.
 *
 * The server checks the same thing (PassportNumber rule); this is the courtesy,
 * not the lock.
 *
 * Starting country: the one saved with the number (or the old input), else the
 * visitor's country by IP, else the field's default.
 */
const flag = code => code.replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)))
const fold = s => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export default {
    init() {
        document.querySelectorAll('input[data-exo-passport]').forEach(el => this.mount(el))
    },

    mount(input) {
        if (input.dataset.exoPassportMounted || !input.name) return
        input.dataset.exoPassportMounted = '1'

        let cfg = {}
        try { cfg = JSON.parse(input.dataset.exoPassport || '{}') } catch (e) { /* defaults */ }
        const L = cfg.labels || {}
        const countries = cfg.countries || {}
        const patterns = cfg.patterns || {}
        const regex = {}
        const patternFor = code => {
            if (!regex[code]) regex[code] = new RegExp('^(?:' + (patterns[code] || cfg.fallback || '.*') + ')$')
            return regex[code]
        }

        // Layout: [picker | input] in a wrapper, the country list panel and the
        // error message under it.
        const wrap = document.createElement('div')
        wrap.className = 'exo-passport'
        input.parentNode.insertBefore(wrap, input)

        const picker = document.createElement('button')
        picker.type = 'button'
        picker.className = 'exo-passport__picker'
        picker.setAttribute('aria-haspopup', 'listbox')
        picker.setAttribute('aria-expanded', 'false')
        picker.setAttribute('aria-label', L.country || 'Issuing country')
        picker.innerHTML = '<span class="exo-passport__flag"></span><span class="exo-passport__code"></span>' +
            '<span class="exo-passport__caret" aria-hidden="true">▾</span>'

        const hidden = document.createElement('input')
        hidden.type = 'hidden'
        hidden.name = input.name + '_country'

        const panel = document.createElement('div')
        panel.className = 'exo-passport__panel'
        panel.hidden = true
        panel.innerHTML = '<input type="text" class="exo-passport__search" autocomplete="off" spellcheck="false">' +
            '<ul class="exo-passport__list" role="listbox"></ul>'
        const search = panel.querySelector('.exo-passport__search')
        const list = panel.querySelector('.exo-passport__list')
        search.placeholder = L.search || 'Search'
        search.setAttribute('aria-label', L.search || 'Search')

        const items = Object.keys(countries).map(code => {
            const li = document.createElement('li')
            li.className = 'exo-passport__option'
            li.setAttribute('role', 'option')
            li.dataset.code = code
            li.dataset.key = fold(countries[code] + ' ' + code)
            li.innerHTML = '<span class="exo-passport__flag"></span><span class="exo-passport__name"></span>' +
                '<span class="exo-passport__option-code"></span>'
            li.children[0].textContent = flag(code)
            li.children[1].textContent = countries[code]
            li.children[2].textContent = code
            list.appendChild(li)
            return li
        })

        const error = document.createElement('small')
        error.className = 'exo-passport__error'
        error.setAttribute('aria-live', 'polite')
        error.hidden = true

        // A green check at the end of the field once the number fits its
        // country's format — the same mark the phone field shows when verified.
        const ok = document.createElement('span')
        ok.className = 'exo-passport__ok'
        ok.hidden = true
        ok.setAttribute('role', 'img')
        ok.setAttribute('aria-label', L.valid || 'Valid format')
        ok.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'

        wrap.appendChild(picker)
        wrap.appendChild(input)
        wrap.appendChild(hidden)
        wrap.appendChild(ok)
        wrap.appendChild(panel)
        wrap.after(error)

        // ---- validity -------------------------------------------------------
        const inputRequired = input.required
        let shown = false   // the visitor has left the field once: show errors from now on
        const check = () => {
            const value = input.value
            const bad = value !== '' && !patternFor(hidden.value).test(value)
            input.required = inputRequired || value !== ''
            input.setCustomValidity(bad ? (L.invalid || 'Invalid') : '')
            const showBad = bad && shown
            const good = value !== '' && !bad
            ok.hidden = !good
            wrap.classList.toggle('exo-passport--valid', good)
            wrap.classList.toggle('exo-passport--invalid', showBad)
            error.hidden = !showBad
            error.textContent = showBad
                ? (L.invalidFor || L.invalid || 'Invalid').replace(':country', countries[hidden.value] || hidden.value)
                : ''
            // Let every submit gate see the new validity (see the note above).
            input.dispatchEvent(new Event('change', { bubbles: true }))
        }

        // ---- country ------------------------------------------------------------
        let touched = false
        const choose = (code) => {
            code = String(code || '').toUpperCase()
            if (!countries[code]) return false
            hidden.value = code
            picker.querySelector('.exo-passport__flag').textContent = flag(code)
            picker.querySelector('.exo-passport__code').textContent = code
            items.forEach(li => li.setAttribute('aria-selected', li.dataset.code === code ? 'true' : 'false'))
            check()
            return true
        }

        // ---- the country list -------------------------------------------------
        let active = -1
        const visible = () => items.filter(li => !li.hidden)
        const highlight = (index) => {
            const shownItems = visible()
            items.forEach(li => li.classList.remove('is-active'))
            if (!shownItems.length) { active = -1; return }
            active = Math.max(0, Math.min(index, shownItems.length - 1))
            shownItems[active].classList.add('is-active')
            shownItems[active].scrollIntoView({ block: 'nearest' })
        }
        const filter = () => {
            const q = fold(search.value.trim())
            items.forEach(li => { li.hidden = q !== '' && !li.dataset.key.includes(q) })
            highlight(0)
        }
        const open = () => {
            panel.hidden = false
            picker.setAttribute('aria-expanded', 'true')
            search.value = ''
            filter()
            const current = visible().findIndex(li => li.dataset.code === hidden.value)
            highlight(current < 0 ? 0 : current)
            search.focus()
        }
        const close = (focusPicker) => {
            if (panel.hidden) return
            panel.hidden = true
            picker.setAttribute('aria-expanded', 'false')
            if (focusPicker) picker.focus()
        }
        const pick = (li) => {
            if (!li) return
            touched = true
            choose(li.dataset.code)
            close(false)
            input.focus()
        }

        picker.addEventListener('click', () => (panel.hidden ? open() : close(true)))
        search.addEventListener('input', filter)
        search.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') { highlight(active + 1); e.preventDefault() }
            else if (e.key === 'ArrowUp') { highlight(active - 1); e.preventDefault() }
            else if (e.key === 'Enter') { pick(visible()[active]); e.preventDefault() }
            else if (e.key === 'Escape') { close(true); e.preventDefault() }
            else if (e.key === 'Tab') { close(false) }
        })
        list.addEventListener('mousedown', e => e.preventDefault())   // keep focus in the search box
        list.addEventListener('click', e => pick(e.target.closest('.exo-passport__option')))
        document.addEventListener('mousedown', (e) => { if (!wrap.contains(e.target)) close(false) })

        // ---- the number -------------------------------------------------------
        // No spaces, capitals — keeping the caret where it was.
        input.addEventListener('input', () => {
            const before = input.value
            const clean = before.replace(/\s+/g, '').toUpperCase()
            if (clean !== before) {
                const caret = input.selectionStart - (before.slice(0, input.selectionStart).match(/\s/g) || []).length
                input.value = clean
                try { input.setSelectionRange(caret, caret) } catch (e) { /* not focusable */ }
            }
            check()
        })
        input.addEventListener('blur', () => {
            if (input.value !== '') shown = true
            check()
        })

        input.value = input.value.replace(/\s+/g, '').toUpperCase()
        // A saved number is judged at once: it is not being typed.
        if (input.value !== '') shown = true
        if (!choose(input.dataset.exoPassportCountry)) {
            choose(cfg.country) || choose(Object.keys(countries)[0])
            // Only if the visitor has not picked one themselves meanwhile.
            geoCountry().then(code => { if (!touched) choose(code) })
        }
    },
}
